import { useState } from "react";
import { useParams } from "react-router-dom";
import { BrandLockup } from "@/shell/CandidateFlow";

export function RecruiterReset() {
  const { token } = useParams<{ token: string }>();

  const [password, setPassword]               = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy]                       = useState(false);
  const [error, setError]                     = useState<string | null>(null);
  const [success, setSuccess]                 = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setBusy(true);

    try {
      // 1. Fetch a fresh CSRF token.
      const csrfRes = await fetch("/api/v1/csrf-token", { credentials: "include" });
      if (!csrfRes.ok) throw new Error("Could not obtain security token. Please refresh and try again.");
      const csrfData = (await csrfRes.json()) as { token?: string; csrf_token?: string };
      const csrfToken = csrfData.token ?? csrfData.csrf_token ?? "";

      // 2. POST as FormData.
      const body = new FormData();
      body.append("password", password);
      body.append("confirm", confirmPassword);
      body.append("csrf_token", csrfToken);

      const res = await fetch(`/auth/reset/${token}`, {
        method: "POST",
        credentials: "include",
        redirect: "follow",
        headers: { "X-CSRFToken": csrfToken },
        body,
      });

      // Success: Flask redirects to /auth/login.
      if (res.url && res.url.includes("/auth/login")) {
        setSuccess(true);
        setBusy(false);
        return;
      }

      if (res.status === 400 || res.status === 422) {
        try {
          const json = await res.json() as { ok: boolean; error?: string };
          setError(json.error ?? "Invalid or expired reset link.");
        } catch {
          setError("Invalid or expired reset link.");
        }
        setBusy(false);
        return;
      }

      if (!res.ok) {
        setError("Something went wrong. Please request a new reset link.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    }

    setBusy(false);
  }

  const canSubmit = password.length > 0 && confirmPassword.length > 0 && !busy;

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#FBFAFE",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px 16px",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 400,
          background: "#FFFFFF",
          borderRadius: 16,
          padding: 40,
          boxShadow: "0 1px 4px rgba(45,30,90,0.08)",
        }}
      >
        <div style={{ display: "flex", marginBottom: 28 }}>
          <BrandLockup />
        </div>

        {/* Heading */}
        <h1
          style={{
            margin: "0 0 6px",
            fontSize: 26,
            fontWeight: 700,
            color: "#17131F",
            letterSpacing: "-0.4px",
            lineHeight: 1.2,
          }}
        >
          Set new password
        </h1>
        <p
          style={{
            margin: "0 0 28px",
            fontSize: 14,
            color: "#7C7590",
          }}
        >
          Enter your new password below.
        </p>

        {/* Success state */}
        {success ? (
          <div
            style={{
              padding: "16px 18px",
              background: "#F0FDF4",
              border: "1px solid #86EFAC",
              borderRadius: 8,
              fontSize: 14,
              color: "#166534",
              lineHeight: 1.5,
            }}
          >
            Password updated.{" "}
            <a
              href="/auth/login"
              style={{
                color: "#7A6BE0",
                fontWeight: 600,
                textDecoration: "none",
              }}
              onMouseOver={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
              onMouseOut={(e)  => { e.currentTarget.style.textDecoration = "none"; }}
            >
              Sign in below.
            </a>
          </div>
        ) : (
          <>
            {/* Error alert */}
            {error && (
              <div
                role="alert"
                style={{
                  marginBottom: 20,
                  padding: "12px 14px",
                  background: "#FEF2F2",
                  border: "1px solid #FCA5A5",
                  borderRadius: 8,
                  fontSize: 13.5,
                  color: "#B91C1C",
                }}
              >
                {error}
              </div>
            )}

            <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              {/* New password field */}
              <div>
                <label
                  htmlFor="reset-password"
                  style={{
                    display: "block",
                    fontFamily: "var(--font-mono, 'Geist Mono', monospace)",
                    fontSize: 10,
                    fontWeight: 600,
                    letterSpacing: "0.10em",
                    textTransform: "uppercase",
                    color: "#7C7590",
                    marginBottom: 8,
                  }}
                >
                  New Password
                </label>
                <input
                  id="reset-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoFocus
                  autoComplete="new-password"
                  placeholder="••••••••"
                  style={{
                    display: "block",
                    width: "100%",
                    boxSizing: "border-box",
                    background: "#FFFFFF",
                    border: "1px solid #E0DBD3",
                    borderRadius: 8,
                    padding: "12px 16px",
                    fontSize: 14,
                    color: "#17131F",
                    outline: "none",
                  }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = "#7A6BE0"; }}
                  onBlur={(e)  => { e.currentTarget.style.borderColor = "#E0DBD3"; }}
                />
              </div>

              {/* Confirm password field */}
              <div>
                <label
                  htmlFor="reset-confirm-password"
                  style={{
                    display: "block",
                    fontFamily: "var(--font-mono, 'Geist Mono', monospace)",
                    fontSize: 10,
                    fontWeight: 600,
                    letterSpacing: "0.10em",
                    textTransform: "uppercase",
                    color: "#7C7590",
                    marginBottom: 8,
                  }}
                >
                  Confirm New Password
                </label>
                <input
                  id="reset-confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  placeholder="••••••••"
                  style={{
                    display: "block",
                    width: "100%",
                    boxSizing: "border-box",
                    background: "#FFFFFF",
                    border: "1px solid #E0DBD3",
                    borderRadius: 8,
                    padding: "12px 16px",
                    fontSize: 14,
                    color: "#17131F",
                    outline: "none",
                  }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = "#7A6BE0"; }}
                  onBlur={(e)  => { e.currentTarget.style.borderColor = "#E0DBD3"; }}
                />
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={!canSubmit}
                style={{
                  width: "100%",
                  height: 48,
                  background: !canSubmit ? "#4B5563" : "#17131F",
                  color: "#FFFFFF",
                  border: "none",
                  borderRadius: 9999,
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: !canSubmit ? "default" : "pointer",
                  opacity: !canSubmit ? 0.6 : 1,
                  transition: "opacity 0.12s, background 0.12s",
                  letterSpacing: "0.01em",
                }}
              >
                {busy ? "Updating…" : "Update password"}
              </button>
            </form>
          </>
        )}

        {/* Back to sign in */}
        <p
          style={{
            marginTop: 24,
            textAlign: "center",
            fontSize: 13.5,
            color: "#7C7590",
          }}
        >
          <a
            href="/auth/login"
            style={{
              color: "#7A6BE0",
              textDecoration: "none",
              fontWeight: 500,
            }}
            onMouseOver={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
            onMouseOut={(e)  => { e.currentTarget.style.textDecoration = "none"; }}
          >
            Back to sign in
          </a>
        </p>
      </div>
    </div>
  );
}
